// One queue per gallery. Visible images never wait for the optional directory or
// a concurrency limit; only speculative requests share a single background slot.
export function createImageLoadingQueue() {
    const jobs = new Set();
    let pauses = 0;
    let destroyed = false;

    function cancel(job) {
        const request = job.request;
        job.request = null;
        if (request) request.cancel();
    }

    function start(job) {
        if (job.request || job.done || job.failed) return;
        const request = job.load();
        job.request = request;
        request.promise.then(value => {
            if (job.request !== request) return;
            job.done = true;
            job.onLoad(value);
        }, error => {
            if (job.request !== request) return;
            job.failed = true;
            job.onError(error);
        }).finally(() => {
            if (job.request === request) {
                job.request = null;
                flush();
            }
        });
    }

    function flush() {
        if (destroyed) return;
        let background = null;
        jobs.forEach(job => {
            if (job.priority === 'hidden') cancel(job);
            else if (job.priority === 'visible') start(job);
            else if (job.request) {
                // Scrolling can demote several in-flight visible requests.
                if (background) cancel(job);
                else background = job;
            }
        });
        if (!pauses && !background) {
            const next = Array.from(jobs).find(job => job.priority === 'near' && !job.done && !job.failed);
            if (next) start(next);
        }
    }

    return {
        register({load, onLoad, onError}) {
            const job = {load, onLoad, onError, priority: 'hidden', request: null, done: false, failed: false};
            jobs.add(job);
            return {
                setPriority(priority) { job.priority = priority; flush(); },
                retry() { job.failed = false; job.done = false; flush(); },
                // Vue destroys a whole collapsed object synchronously. Let every
                // child unregister before starting another speculative request.
                dispose() { jobs.delete(job); cancel(job); Promise.resolve().then(flush); }
            };
        },
        pauseBackground() {
            pauses++;
            let released = false;
            return () => {
                if (released) return;
                released = true;
                pauses--;
                flush();
            };
        },
        destroy() {
            destroyed = true;
            jobs.forEach(cancel);
            jobs.clear();
        }
    };
}
